import Immutable        from 'immutable';
import {Maybe}          from 'monet';
import {Nothing}        from 'monet';
import {Model}  from 'backbone';

import xjService        from 'xml/XJService';
import themeService     from 'ThemeService';
import zLib             from 'util/ZLib';
import constants        from 'Constants';
import restService      from 'RestService';
import listService      from 'ListService';
import util             from 'util/Util';
import assetCollection      from 'AssetCollection';
import AccountService       from 'AccountService';
import formulaService       from 'xml/FormulaService';
import notificationService  from 'NotificationService';
import guidUtil             from 'util/GuidUtil';
import mobileSaveService    from 'mobile/MobileSaveService';
import userService          from 'UserService';
import formModel            from 'formModel';
import saveHelperService    from 'SaveHelperService';
import formMetadataService    from 'FormMetadataService';
import Spinner from 'spin/spin';
import undoService      from 'UndoService.js';
import ConfirmDialogView    from 'common-views/ConfirmDialogView.js';


const EMPTY = constants.STRING_EMPTY;
const ID_ALL = 'all';
const V_NORMAL = 'normal';
const defaultFont = (listService.accountSettings && listService.accountSettings.DefaultFontSelected) || "verdana";

// TODO revisit property inclusions/exclusions as project proceeds.

// We filter-include these
const formPropInclusions = Immutable.Set([
    'confirmBrowserClose', 'outsideColor', 'rtl', 'centerHorizontal', 'frameRoundCorners', 'showNameOfLockingUser',
    'title', 'pageCounter', 'centerVertical', 'frameColor', 'diagramsGridHeight', 'dpi', 'width', 'pageEffect',
    'sizePreset', 'height', 'shadow', 'isAccessible', 'frameSize', 'isMultiLang=', 'frame', 'pageTopScroll',
    'diagramsGridWidth', 'gridWidth', 'unit', 'timeout', 'isUntitled', 'gridHeight', 'nameCounter', 'responsive',
    'isYodaFile','focusColor'
]);

// We filter-include these
const pagePropInclusions = Immutable.Set([
    'padding', 'backgroundColor', 'transparentBackground', 'titleText', 'border', 'backgroundImageAssetId',
    'borderColor', 'preloadImage', 'hasTitle', 'top', 'accessDesc', 'titleColor', 'zOrder', 'titleFont',
    'left', 'transparency', 'obligatoryMessage', 'tabEnabled', 'accessSilent', 'masterCtrl', 'backgroundImageStyle',
    'master', 'titleBackgroundColor', 'titleHeight', 'toolTip', 'titleAlign', 'width', 'height'
]);

// Interim forced form properties JSON for save.
const formPropAdditions = {
    "_isAccessible" : "true",
    "_gridHeight" : "20",
    "_confirmBrowserClose" : "true",
    "_title" : "May Test Form",
    "_frameColor" : "ffffff",
    "_frameSize" : "16",
    "_isMultiLang" : "false",
    "_pageTopScroll" : "true",
    "_isUntitled" : "true",
    "_dpi" : "72",
    "_pageCounter" : "0",
    "_shadow" : "true",
    "_diagramsGridHeight" : "25",
    "_unit" : "pixel",
    "_frame" : "false",
    "_gridWidth" : "20",
    "_rtl" : "false",
    "_nameCounter" : "3",
    "_centerVertical" : "true",
    "_showNameOfLockingUser" : "true",
    "_sizePreset" : "custom",
    "_centerHorizontal" : "true",
    "_diagramsGridWidth" : "25",
    "_timeout" : "30",
    "_pageEffect" : "none",
    "_outsideColor" : "eeeeee",
    "_frameRoundCorners" : "true"
};


// Interim forced page properties JSON XML attribute notation for save.
const pagePropAdditions = {
    "_preloadImage" :  "true",
    "_hasTitle" :  "false",
    "_titleColor" :  "FFFFFF",
    "_accessDesc" :  "",
    "_titleFont" :  defaultFont + ", 10",
    "_master" :  "no",
    "_tabEnabled" :  "false",
    "_masterCtrl" :  "",
    "_backgroundColor" :  "FFFF99", // Placeholder. Page turns yellow in AS because of this.
    "_transparency" :  "1",
    "_obligatoryMessage" :  "",
    "_accessSilent" :  "false",
    "_titleBackgroundColor" :  "0099FF",
    "_backgroundImageStyle" :  "center",
    "_border" :  "1, solid",
    "_titleHeight" :  "20",
    "_borderColor" :  "333333",
    "_titleAlign" :  "left",
    "_transparentBackground" :  "false",
    "_toolTip" :  "",
    "_padding" :  "0",
    "_titleText" :  ""
    //"_backgroundImageAssetId" : "~groupBgImage"
};


//===========================================================================================
// This service has a method to emit the current state of listService to a JS object
// suitable for conversion to XML. We designate a key as an XML attribute name by naming
// it with a leading underscore.
//
// We decompose the task into simple, almost pure functions.++ We safely chain them,
// making the task easier to reason about.
//
// Our implementation uses a "Maybe" monad from the Monet library. It aids in safely
// chaining functions is a manner resistent to undefined and null values.## If there were
// that kind of failure, the result would be a "Nothing" that resolved to "undefined".
// The idea is that an "undefined" somewhere in the chain of functions will cause
// successive functions to skip processing instead of throwing an exception. It's okay
// for the caller to receive a graceful "Nothing" provided that it tests for it.
//
// Lou Mauget March 19, 2016
//
// ++ We imported references to listService and themeService. These would be passed in a
// parameter object if the functions were totally pure.
//
// ## A monad is a data wrapper that hides its wrapped data from the outside world.
// A function mapped into the monad via a chain method (aka bind, or flapMap), gets
// access to the monad data as its parameter. It operates on that data before returning
// it into the monad as an intermediate or final result. We chained our functions
// to other chain functions and even to other kinds of monads.
//
// We chose a "Maybe" mondad that won't call downstream functions past a point
// where one of them has trouble, causing it to return a "Nothing" value. This is a
// functional way to handle execeptional conditions that could cause a downstream function
// to throw an exception.
//
// See documentation on Monet in its README.md in npm_modules.
//===========================================================================================

let self = undefined;
class FormSaveService {

    constructor() {
        self = this;
    }

    // Return XML / JSON doucment root name for current kind of document loaded in system.
    get docRoot() { return listService.docType === constants.DOC_TYPE_REPORT ? 'Report' : 'Form'; }



    setColorValue( props, targetName, value) {
        if (value) {
            props[targetName] = value.replace('#','');
        }
    }

    // static get user() { return JSON.parse(window.localStorage.getItem('user') );}
    get user() { return userService.userSettings; }


    //==========================================================
    // Assembles form structures into a JavaScript Form object;
    //==========================================================
    marshalForm( listService ) {

        const root = self.docRoot;

        const formRoot = function(f) {
            const fb = listService.formBase;
            f[root] = {};
            f[root]._id = fb.id;
            f[root]._name = listService.currentForm.model.get('name') || fb.name;
            return Maybe.fromNull(f);
        };

        const formGuid = function (f) {
            f[root]._guid = listService.formBase.guid;
            return Maybe.fromNull(f);
        };


        // "_docVersion": "0",
        const formDocVersion = function (f) {
            f[root]._docVersion = listService.formBase.docVersion;
            return Maybe.fromNull(f);
        };


        // "_version": "0.1",
        const formVersion = function (f) {
            f[root]._version = listService.formBase.version;
            return Maybe.fromNull(f);
        };


        //-------------------------------------------------
        //"Properties": {
        //    "_pageEffect": "none",
        //        "_frameSize": "16",
        //        "_isMultiLang": "false",
        //        "_diagramsGridHeight": "25",
        //        "_title": "Empty Form",
        //        "_gridWidth": "20",
        //        ...
        //-------------------------------------------------
        const formProperties = function (f) {
            // const fp = f.Form.Properties = {};
            const fp = f[root].Properties = formPropAdditions; // Interim until form new / save / load / save propagates all properties.
            let val, k2;
            Immutable.List(listService.currentForm.model.keys())
                .filter( k => k.charAt(0) !== '~' )
                .filter( k => formPropInclusions.includes( k ) )
                .map( k => {
                    val = listService.currentForm.model.get(k);

                    if (k === 'width' ) {
                        val = listService.currentForm.model.get('formWidth');
                    }
                    if ( k === 'height') {
                        val = listService.currentForm.model.get('formHeight');
                    }

                    if (k === 'title') {
                        if (util.isEmpty(listService.currentForm.model.get('title'))) {
                            val = listService.currentForm.model.get('name');
                        }
                    }

                    if (val && k.toLowerCase().includes('color')) {
                        val = val.replace('#','');
                    }

                    k2 = `_${k}`;  // Mark name as an XML attribute for JSON2XML conversion
                    fp[k2] = val;
                });

            return Maybe.fromNull(f);
        };


        //---------------------------------------------------
        //"States": {
        //    "State": {
        //        "_id": "all_all",
        //            "_value": "normal"
        //    }
        //},
        //---------------------------------------------------
        const states = function (f) {
            f[root].States = {State:[]};
            const st ={};
            st._id = `${ID_ALL}_${ID_ALL}`;
            st._value = V_NORMAL;
            if (listService.currentForm.model.get('viewingState')) {
                st._value = listService.currentForm.model.get('viewingState');
            }
            f[root].States.State.push(st);
            if(listService.currentForm.model.get('state') && listService.currentForm.model.get('state').size > 0){
                let disabledAndReadOnlyStates=[...listService.currentForm.model.get('state')].filter(([key, val]) => val.value === 'disabled' || val.value === 'readonly' || val.value === 'normal');
                if(disabledAndReadOnlyStates.length > 0){

                    disabledAndReadOnlyStates.forEach((elem)=>{    

                        if (elem[0] !== 'all_all') { //The default 'all_all' state was already added to the array
                            const stDisabled = {};
                            stDisabled._id = elem[0];
                            stDisabled._value = elem[1].value;
                            f[root].States.State.push(stDisabled);
                        }
                    });
                }
            }
            return Maybe.fromNull( f );
        };


        //---------------------------------------------------
        // * Examples of Page objects *
        //       <Object id="eCdcaAAA5csAACkIAAAR+Bf3NCE=" type="group" name="Page 1">
        //             <Properties backgroundColor="~pageBgColor" titleText="" border="1, solid" preloadImage="true" backgroundImageAssetId="~pageBgImage" borderColor="cccccc" hasTitle="false" accessDesc="" titleColor="FFFFFF" top="0" left="0" padding="0" width="500" titleFont="verdana, 10" tabEnabled="true" height="400" toolTip="" transparency="1" masterCtrl="" obligatoryMessage="" accessSilent="false" zOrder="0" titleBackgroundColor="0099FF" master="no" backgroundImageStyle="center" titleHeight="20" titleAlign="left" transparentBackground="false" />
        //       </Object>
        //       <Object id="FcToBgAA6/sAAKfEAACxfYzJdWg=" type="group" name="Page 2">
        //             <Properties backgroundColor="~pageBgColor" titleText="" border="1, solid" preloadImage="true" backgroundImageAssetId="~pageBgImage" borderColor="cccccc" hasTitle="false" accessDesc="" titleColor="FFFFFF" top="0" left="0" padding="0" width="500" titleFont="verdana, 10" tabEnabled="true" height="400" toolTip="" transparency="1" masterCtrl="" obligatoryMessage="" accessSilent="false" zOrder="0" titleBackgroundColor="0099FF" master="no" backgroundImageStyle="center" titleHeight="20" titleAlign="left" transparentBackground="false" />
        //       </Object>
        //       <Object id="dDhkTwAAq5cAADQ9AACeCf8K6u0=" type="group" name="Page 3">
        //             <Properties backgroundColor="~pageBgColor" titleText="" border="1, solid" preloadImage="true" backgroundImageAssetId="~pageBgImage" borderColor="cccccc" hasTitle="false" accessDesc="" titleColor="FFFFFF" top="0" left="0" padding="0" width="500" titleFont="verdana, 10" tabEnabled="true" height="400" toolTip="" transparency="1" masterCtrl="" obligatoryMessage="" accessSilent="false" zOrder="0" titleBackgroundColor="0099FF" master="no" backgroundImageStyle="center" titleHeight="20" titleAlign="left" transparentBackground="false" />
        //       </Object>
        //
        // * Objects in general *
        // Objects
        //   Object (oage) <Object id="bUnZXQAAJ+wAAJdkAADyB1DZeO0=" type="group" name="Page 1">
        //     Properties
        //     Objects
        //       Object (group) <Object id="1q7WNAAAPdoAAKYCAACAgSKr12o=" type="group" name="Radio Button Group 13">
        //         Properties
        //         Objects
        //---------------------------------------------------
        const objects = function (f) {

            const root = self.docRoot;

            const objs = f[root].Objects = {}; // outermost - single container of Objects
            const obj = objs.Object = [];       //

            // Traverse the page list, creating object lineage for each
            //console.time("OBJECTS");
            let pageObj, pageId, pgProps, val, key;
            listService.pageList.map( (pageView) => {

                // Object for a page
                pageObj = {};
                pageObj._id = listService.getCleanedId(pageView.model.get('id'));
                pageId = pageView.model.get('id');
                pageObj._type = 'group';
                pageObj._name = pageView.model.get('name');
                obj.push(pageObj);
                

                // Properties of the page object
                // TODO Interim until each propogated through new / save / load / save.
                pgProps = pageObj.Properties = _.clone(pagePropAdditions);
                Immutable.List(pageView.model.keys())
                    .filter( k => pagePropInclusions.includes( k ) )
                    .forEach( k1 => {
                        val = pageView.model.get(k1);

                        if (k1 === 'width' ) {
                            val = listService.currentForm.model.get('formWidth');
                        }
                        if (k1 === 'height') {
                            val = listService.currentForm.model.get('formHeight');
                        }

                        if (k1 === 'backgroundColor') {
                            //val = 'FFFFFF';  // TODO This is why page renders white. Was blowing up on bad css color otherwise
                        }

                        if (val && k1.toLowerCase().includes('color')) {
                            val = val.replace('#','');
                        }

                        key = `_${k1}`;
                        pgProps[key] = val;
                    });
                    
                    //Render States
                    saveHelperService._renderObjectStates(pageView, pageObj);

              
                saveHelperService.behaviorsSaveHelper(pageObj, pageView);
              

                // Drill-down: controls on current page object.
                // Each control has a pageId; it is equal to the pageId above
                pageObj.Objects = {};  // Inner container of Object(s)
                if (typeof pageView.model.get('parsedItem') === 'undefined' || !listService.isPartialSave){
                    const parent = pageObj.Objects;
                    saveHelperService.objectsHelper( parent, pageId, pageView );
                    let currentPageId = listService.currentPageId;
                    if (listService.mainKind === constants.KIND_BEHAVIORS){
                        const instance = listService.getBehaviorInstance(listService.currentBehaviorInstanceId);
                        if (typeof instance !== 'undefined'){
                            currentPageId = instance.get('pageId');
                        }
                    }
                    if (currentPageId !== pageId && typeof parent !== 'undefined'){ //Don't store current page because it can still be modified
                        pageView.model.set('parsedItem', _.cloneDeep(parent), constants.MODEL_SILENT);
                    }
                }else{
                    pageObj.Objects = _.cloneDeep(pageView.model.get('parsedItem'));
                }

            });

            //console.timeEnd("OBJECTS");

            return Maybe.fromNull(f);
        };


        //---------------------------------------------------
        //"Pages": {
        //    "Page": [
        //        {
        //            "_id": "eCdcaAAA5csAACkIAAAR+Bf3NCE=",
        //            "_name": "Page 1"
        //        },
        //        {
        //            "_id": "FcToBgAA6/sAAKfEAACxfYzJdWg=",
        //            "_name": "Page 2"
        //        },
        //        {
        //            "_id": "dDhkTwAAq5cAADQ9AACeCf8K6u0=",
        //            "_name": "Page 3"
        //        }
        //    ]
        //},
        //---------------------------------------------------
        const pages = function (f) {

            const root = self.docRoot;

            const pp = f[root].Pages = {};
            const pa = pp.Page = [];

            let p;
            listService.pageList.map( (v) => {
                p = {};
                p._id = listService.getCleanedId(v.model.get('id'));
                p._name = v.model.get('name');
                pa.push(p);
            });
            
            return Maybe.fromNull(f);
        };

        //---------------------------------------------------
        //     <StartPages>
        //         <StartPage id="PFXX6302947887659614208_all" value="PFXX6302949042468945920" />
        //         <StartPage id="all_all" value="PFXX6300388753584160769" />
        //         <StartPage id="PFXX6302947876821532672_all" value="U4wdJAAAuLkAALN1AAARb+PMCCo=" />
        //         <StartPage id="all_PFXX6302948135597506560" value="default" />
        //         <StartPage id="PFXX6302947869846405120_PFXX6303036414640521216" value="PFXX6300388753584160769" />
        //         <StartPage id="all_PFXX6302948148587266048" value="default" />
        //         <StartPage id="PFXX6302947887659614208_PFXX6303036414640521216" value="PFXX6300388753584160769" />
        //         <StartPage id="PFXX6302947869846405120_all" value="default" />
        //         <StartPage id="PFXX6302947876821532672_PFXX6303036414640521216" value="PFXX6300388753584160769" />
        //     </StartPages>
        //---------------------------------------------------
        const startPages = function (f) {

            // Note: be careful with final 's' on elements. Sorry. It's an artifact of the XML form given to us.
            const modelStartPages = listService.currentForm.model.get('startPages');
            if (modelStartPages && modelStartPages.size) {
                const outStartPages = [];

                modelStartPages.forEach( (item, key) => {
                    outStartPages.push( { _id: key, _value: item.value } )
            });
                f[self.docRoot].StartPages = { StartPage: outStartPages };
            }

            return Maybe.fromNull(f);
        };

        
        const multilanguage = function (f) {

            //Only save if multilanguage is not empty
            if (util.isTranslationEnabled()) {
                const root = self.docRoot;
                const json = JSON.stringify(listService.multilanguage);
                const multilang = zLib.compressForm(json);
                const multilangs = f[root].Multilanguage = { _json: multilang };
            }
            return Maybe.fromNull(f);
        }

        const advancedCSS = function (f) {

            const root = self.docRoot;
            const css = zLib.compressForm(listService.advancedCSS);
            const advCSS = f[root].AdvancedCSS = { _enabled: listService.isAdvancedCSSEnabled, _css: css };
            return Maybe.fromNull(f);
        }

     

        //---------------------------------------------------
        //"Theme": {
        //    "Properties": {
        //        "_mediumHeadingTextColor": "000033",
        //            "_buttonTextColor": "000000",
        //            "_selectionTextOverColor": "111111",
        //            "_pageBgColor": "ffffff",
        //            "_buttonBgDownColor": "eeeeee",
        //            "_seperatorSize": "4",
        //---------------------------------------------------
        const theme = function (f) {

            const root = self.docRoot;

            const t = f[root].Theme = {_id: ''};
            const p = t.Properties = {};

            const toClean = ['groupFont', 'textFont', 'inputFont', 'selectionFont', 'buttonFont'];
            let valueTemp, newKey;
            Immutable.Map( themeService.theme )
                .filter( (value, key) =>  key.charAt(0) === '~'  )
                .map((value, key) => {
                    valueTemp = value;
                    newKey = key.substr(1); 
                    // If it's a theme property that needs cleaning
                    if (toClean.indexOf(newKey) !== -1) {
                        // Remove all "!important" words
                        valueTemp = valueTemp.replace(new RegExp('!important', 'g'), '').split(/[,]+/).join(', '); 
                    }

                    return p[`_${newKey}`] = util.trimLeadingHash(valueTemp);
                });

            return Maybe.fromNull(f);
        };


        //---------------------------------------------------
        //     <Workflow id="" type="workflow">
        //         <Properties height="600" width="600" defaultStageId="..." />
        //         <Stages/>
        //         <Paths/>
        //         <Roles/>
        //     </Workflow>
        //---------------------------------------------------
        const workflow = function (f) {
            console.time(`SAVING WF Stages`);
            // No workflow XML if really a report not a form
            if ( constants.DOC_TYPE_REPORT === listService.docType ){
                return Maybe.fromNull(f);
            }

            // Stages holds stage items and comment items
            // Each item has an id and a type (either 'comment' or 'stage')
            // Each item contains a Properties element
            function emitStages( ) {

                function emitStageItems( stages ) {
                    const acc = { Stage: [] };
                    let obj, model, p;
                    listService.workflowStages.forEach( v => {
                       // console.log(`stage`, v);

                        obj = {};
                        model = v.model;

                        obj._id = model.get('id');
                        obj._type = 'stage';
                        obj.Properties = {};

                        p = obj.Properties;
                        p._x = model.get('x');
                        p._y = model.get('y');
                        p._width = model.get('width');
                        p._height = model.get('height');

                        p._name = model.get('name');
                        p._isClosed = model.get('isClosed') || 'false';
                        p._description = model.get('description');

                        acc.Stage.push( obj );
                    });
                    stages.push(acc);
                }

                function emitCommentItems( stages ) {
                    const acc = { Comment: [] };

                    let obj, model, p;
                    listService.workflowComments.forEach( v => {
                        console.log(`comment`, v);

                        obj = {};
                        model = v.model;

                        obj._id = model.get('id');
                        obj._type = 'comment';
                        obj.Properties = {};

                        p = obj.Properties;
                        p._x = model.get('x');
                        p._y = model.get('y');
                        p._width = model.get('width');
                        p._height = model.get('height');

                        p._text = model.get('text');

                        acc.Comment.push( obj );
                    });
                    stages.push(acc);
                }

                const stages = [];

                emitStageItems( stages  );
                emitCommentItems( stages );

                // Hack to collapse comments into parent as stage elements
                if (stages.length > 1) {
                    stages[0].Comment = stages[1].Comment;
                    return stages.slice(0, 1);
                }

                return stages;
            }


            // <Workflow  Paths (connections)
            // <Paths>
            //     <Path id="6HtYJAAAQqYAANsDAAAd4Nbfots=" type="workflowconnector">
            //         <Properties sourcePoint="4" textBoxWidth="80" sourceShape="ow4NwQAAyLIAAMq5AABmP5mj5Eo=" targetPoint="10" ctrlPoint2x="" ctrlPoint1x="" textBoxHeight="4" targetShape="PF_58" lineType="straight" name="" ctrlPoint1y="" ctrlPoint2y="" />
            //         <Parameters>
            //             <Parameter id="default" type="expression" literal="null" field="null" special="null">
            //                 <Expression id="djoqMgAAzi0AAGmjAADlrVdipsM=" type="scriptingCondition">
            //                 <Argument id="OylKWgAAa/wAAIJuAACYe6SXde8=" type="scripting_literal" value="'string'" />
            //                 <Operator id="vAweggAAC8wAAFfDAAAf27TXlyE=" type="==" />
            //                 <Argument id="FVmMPAAA6t0AAG3wAABJVgHnBk4=" type="scripting_date" value="2016-09-01 00:00:00" />
            //                 </Expression>
            //             </Parameter>
            //         </Parameters>
            //     </Path>
            //     <Path id="darUywAAcQQAAE24AADleJevQok=" type="workflowconnector">
            //         <Properties sourcePoint="4" textBoxWidth="80" sourceShape="GYnKDAAAPBkAAOLyAADP4YpLpnE=" targetPoint="10" ctrlPoint2x="" ctrlPoint1x="" textBoxHeight="4" targetShape="E4ZiKwAA3k4AAFSdAAA825FzeDg=" lineType="straight" name="" ctrlPoint1y="" ctrlPoint2y="" />
            //     </Path>
            //      <Path id="ptu3GAAAlYYAAC0XAADthqFcvlM=" type="workflowconnector">
            //         <Properties sourcePoint="4" textBoxWidth="80" sourceShape="E4ZiKwAA3k4AAFSdAAA825FzeDg=" targetPoint="10" ctrlPoint2x="" ctrlPoint1x="" textBoxHeight="24" targetShape="ow4NwQAAyLIAAMq5AABmP5mj5Eo=" lineType="straight" name="Path 2" ctrlPoint1y="" ctrlPoint2y="" />
            //     </Path>
            // </Paths>
            function emitPaths( ) {

                const paths = { Path: [] };

                // Map NESW points 0..4 to stage pts 0..11 t
                //             0, 1, 2, 3, 4, 5, 6, 7, 8, 9,10,11 // clockwise from 1 o'oclok
                //   inmap = [ 0, 0, 0, 1, 1, 1, 2, 2, 2, 3, 3, 3 ];
                //  const pmap = [ 1, 4, 7, 10, 0];
                // PF-3551
                const pmap = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 9,10,11 ];

               // console.log(`FORM SAVE of STORED PATHS`, listService.workflowPaths.toJS());
                let path, props;
                listService.workflowPaths.forEach( v => {
                    console.log(`workflow connector`, v);

                    path = {};
                    path._id = v.id;
                    path._type = 'workflowconnector';

                    props = path.Properties = {};
                    props._sourceShape = v.srcCommand.id;
                    props._targetShape = v.destCommand.id;

                    // Dot index is 1-origin in app. Convert back to PF 0-origin index.
                    props._sourcePoint = pmap[Number(v.srcDot) ];
                    props._targetPoint = pmap[Number(v.destDot) ];

                    // PF-2345 - don't hard-code control points
                    props._ctrlPoint1x = v.model.attributes.ctrlPoint1x;
                    props._ctrlPoint1y = v.model.attributes.ctrlPoint1y;
                    props._ctrlPoint2x = v.model.attributes.ctrlPoint2x;
                    props._ctrlPoint2y = v.model.attributes.ctrlPoint2y;

                    props._text = v.model.attributes.text;

                    // PF-2345 - don't hard-code line type
                    props._lineType    = v.model.attributes.lineType;

                    props._name = v.model.attributes.name;

                    props._textBoxHeight = v.textBoxHeight;
                    props._textBoxWidth = v.model.attributes.textBoxWidth;

                    path.Parameters = {};
                    path.Parameters.Parameter = [];                    
                    if (v.model.attributes.Parameters) {

                        let Parameter;
                        _.forEach(v.model.attributes.Parameters.Parameter, (parameter) => {
                            Parameter = {};

                            for (var p in parameter) {
                                if (parameter.hasOwnProperty(p) && (parameter[p] !== undefined) && (p !== 'EXPR') && (p !== 'Expression')) {
                                    let val = parameter[p];
                                    const key = `_${p}`;

                                    Parameter[key] = val;
                                }
                            }

                            if ( parameter.Expression && typeof parameter.Expression.id !== 'undefined' && parameter.Expression.arguments && parameter.Expression.arguments.length > 0 ){
                                Parameter.Expression = formulaService.convertExpression(parameter.Expression);
                            }

                            path.Parameters.Parameter.push(Parameter);
                        });
                    }
                 
                    saveHelperService.behaviorsSaveHelper(path, v);
                 //    console.log(path);

                    paths.Path.push( path );
                 //   console.log(`PATH emitted`, path);
                });
                return paths;
            }


            // <!-- Like this: -->
            // <Roles>
            //     <Role id="HKGlMQAAi2QAALz2AACkirZyZn0=" name="Developer">
            //         <Properties personName="" personId="lmauget@keyholesoftware.com" personType="3" />
            //     </Role>
            //     <Role id="ktv7fAAAApYAAJ38AACdO+D53V8=" name="User">
            //         <Properties personName="Lou Mauget" personId="-2147479679" personType="1" />
            //     </Role>
            // </Roles>
            function emitRoles() {

                const roles = {Role: []};
                let r, p, personType;
                listService.workflowRoles.forEach(v => {
                    console.log(`ROLE emitting:`, v);
                    
                        r = {Properties: {}};
                        p = r.Properties;

                    r._id = v.id;
                    r._name = v.name || EMPTY;

                    p._personName = v.defaultPerson;
                    p._personId = v.personId;

                    personType = "";
                    switch (v.userRoleType) {
                        case "":
                            personType = EMPTY;
                            break;
                        case "user":
                            personType = "1";
                            break;
                        case "position":
                            personType = "2";
                            break;
                        case "fixedEmail":
                            personType = "3";
                            p._personId = v.defaultPerson;
                            p._personName = EMPTY;
                            break;
                        case "userGroup":
                            personType = "4";
                            break;
                        case "fieldEmail":
                            personType = "5";
                            break;
                    }

                    p._personType = personType;


                    roles.Role.push(r);
                    console.log(`ROLE emitted`, r);
                });

                return roles;
            }

            // API sends/receives constant empty ID, and type workflow.
            const w = f[root].Workflow = {Properties: {}, _id: '', _type: 'workflow'};

            const isStages = listService.workflowStages && listService.workflowStages.size;

            const p = w.Properties;
            p._height = listService.workflowProperties.get('height');
            p._width = listService.workflowProperties.get('width');


            if (isStages) {
                const p = w.Properties;
                p._height = listService.workflowProperties ?
                    listService.workflowProperties.get('height'):'';
                p._width = listService.workflowProperties ?
                    listService.workflowProperties.get('width'):'';

                p._defaultStageId = (listService.getCleanedId( listService.workflowProperties.get('defaultStageId') )
                || isStages ? listService.workflowProperties.get('defaultStageId') : '');
            }

            // <Stages/>    <Paths/>   <Roles/>
            w.Stages = emitStages();

            w.Paths = emitPaths();
            w.Roles = emitRoles();

            try{
                if(w.Paths.Path && w.Paths.Path.length>0){
                    let size=w.Paths.Path.length;
                    for(let i=0;i<size;i++){
                        if(w.Paths.Path[i].Script && !w.Paths.Path[i].Script.Connectors){
                            w.Paths.Path[i].Script.Connectors={Connector: []};
                        }
                    }
                }
            }
            catch(error){}
            console.timeEnd(`SAVING WF Stages`);
            console.log(`SAVING WF Stages`, w.Stages);
            
            return Maybe.fromNull(f);
        };

        //---------------------------------------------------
        // <Assets>
        //        <Asset id="2jNhg2AgAAgASgwJ" type="image" name="android-attitude" mimeType=""/>
        // </Assets>
        //---------------------------------------------------
        const assets = function (f) {
            console.time(`SAVING ASSETS`);
            const root = self.docRoot;

            const assets = f[root].Assets = {};
            const asset = assets.Asset = [];

            assetCollection.forEach( a  => {
                asset.push({
                    _id: a.get('id'),
                    _type: a.get('type'),
                    _mimeType: a.get('mimeType'),
                    _name: a.get('name')
                });

            });
            console.timeEnd(`SAVING ASSETS`);
            return Maybe.fromNull(f);

        };       

        const datasources = function (f) { 
            console.time(`SAVING DATA SOURCES`);
            const root = self.docRoot;

            const dataSources = f[root].DataSources = {};
            const datasource = dataSources.DataSource = [];

            const localDataSources = listService.getDataSources();

            localDataSources.forEach( (v,k) => {
               let newDS = {
                   _id:  listService.getCleanedId(v.id),
                   _type: v.type,
                   _name: v.name,
                   _description: v.description,
                   _formName: v.formName,
                   _useORForDynamicFilters: v.useORForDynamicFilters,
                   _formGUID: v.formGUID
               };

               newDS.Fields = { Field: []};

               _.forEach(v.Fields.Field, (field) => {
                   if (field.meta ) {
                       field.Meta = field.meta.toLowerCase();
                       delete field.meta;
                   }

                   newDS.Fields.Field.push( util.applyPrefix( field ));
               });

               // PF-3078 Defend against empty formula parameter if no static filter.
               const fnIsGoodFormulaParam = ( fp => fp
                   && fp.arguments
                   && Array.isArray(fp.arguments)
                   && fp.arguments.length > 0
                   );

               if ( fnIsGoodFormulaParam(v.formulaParameter) ) {
                    console.log(`v.formulaParameter`, v.formulaParameter);
                    newDS.Filter = {};
                    newDS.Filter.Expression = {};
                    newDS.Filter.Expression = formulaService.convertExpression(v.formulaParameter, true, false, newDS._id);
               }

               datasource.push( newDS ); 
            });
            console.timeEnd(`SAVING DATA SOURCES`);
            return Maybe.fromNull(f);
        };

        const dependencies = function (f) {
            console.time(`SAVING DEPENDENCIES`);
            const root = self.docRoot;
            const Dependencies = f[root].Dependencies = {};
            const Dependency = Dependencies.Dependency = [];

            const _dependencies = listService.getDependencies();

            if (_dependencies && _dependencies.length > 0) {
                $.each(_dependencies, function(idx, _dependency){
                    let _newDependency = {};
                    _newDependency._id = _dependency.id;
                    _newDependency._type = _dependency.type;
                    _newDependency._caption = _dependency.caption;
                    Dependency.push(_newDependency);
                });
            }
            console.timeEnd(`SAVING DEPENDENCIES`);

            return Maybe.fromNull(f);
        };

        const parameters = function (f) {
            console.time(`SAVING PARAMETERS`);
            const root = self.docRoot;
            const Parameters = f[root].Parameters = {};
            Parameters.Parameter = [];

            if (listService.currentForm.model.attributes.Parameters) {
                let Parameter;
                _.forEach(listService.currentForm.model.attributes.Parameters.Parameter, (parameter) => {
                    Parameter = {};

                    for (var p in parameter) {
                        if (parameter.hasOwnProperty(p) && (parameter[p] !== undefined) && (p !== 'EXPR') && (p !== 'Expression')) {
                            let val = parameter[p];
                            const key = `_${p}`;

                            Parameter[key] = val;
                        }
                    }

                    // PF-9576 avoid to add empty expressions
                    if ( parameter.Expression && typeof parameter.Expression.id !== 'undefined' && parameter.Expression.arguments && parameter.Expression.arguments.length > 0 ){
                        Parameter.Expression = formulaService.convertExpression(parameter.Expression);
                    }

                    Parameters.Parameter.push(Parameter);
                });
            }
            console.timeEnd(`SAVING PARAMETERS`);

            return Maybe.fromNull(f);
        };

        const formEvents = function (f) {
            console.time(`SAVING FORM EVENTS`);
            const root = self.docRoot;
            const v = {id: 'Form'};

            saveHelperService.behaviorsSaveHelper(f[root], v);
            console.timeEnd(`SAVING FORM EVENTS`);
            
            return Maybe.fromNull(f);
        };

        const reportingMetaData = function (f) {   
            console.time(`SAVING REPORTING METADATA`);         
            const root = self.docRoot;
            const metaData = f[root].ReportingMetaData = {};
            const metaDataObjects = metaData.Object = [];

            const md = listService.getReportingMetaData();
            let isChart = false;
            md.forEach( (v,k) => {
                let newMetaData = {
                    _id: listService.getCleanedId(v.get('id')),
                    _dataSource: listService.getCleanedId(v.get('dataSource')),
                    _name: v.get('name'),
                    _isAggregate: v.get('isAggregate'),
                    _ignoreNull: v.get('ignoreNull')
                };

                let _control = listService.getObject(v.get('id'));

                if (!_control){
                    return;
                }

                newMetaData._name = _control.model.get('name');

                let isDataGridOrDataList = false;
                if (_control && ['control_data_grid', 'control_data_list'].indexOf(_control.model.get('factoryKey')) !== -1){
                    isDataGridOrDataList = true; //For the case when control is Data Grid or Data list and doesn't have columns
                }

                if (v.Columns || isDataGridOrDataList) { 
                    newMetaData.Columns = {};
                    newMetaData.Columns.Column = [];
                    let obj;
                    _.forEach(v.Columns, (column) => {
                        obj = {_id: column.id,
                                   _aggregation: column.aggregation,
                                   _sortType: column.sortType,
                                   _sortOrder: column.sortOrder,
                                   _visible: column.visible.toString() };
                        if(column.Expression.Argument){
                            if(Array.isArray(column.Expression.Argument)){
                                obj.Expression = formulaService.convertExpression(column.Expression, true);
                                isChart = true;
                            }else{
                                obj.Expression = { Argument: {_type: column.Expression.Argument.type, _value: column.Expression.Argument.value, _dataType: column.Expression.Argument.dataType } };
                            }
                        }else{
                            isChart = true;
                            obj.Expression = { Argument: {_type: 'reporting_datasourceField', _value: '', _dataType: '' } };
                        }
                        newMetaData.Columns.Column.push(obj);
                    });
                }

                if (newMetaData.Columns && newMetaData.Columns.Column.length > 0){
                   if(isChart){
                       metaDataObjects.push(newMetaData);
                   }else if(newMetaData.Columns && newMetaData.Columns.Column[0] && 
                      newMetaData.Columns.Column[0].Expression.Argument._value !== ''){
                       metaDataObjects.push(newMetaData);
                   }
                }else if (isDataGridOrDataList){
                    metaDataObjects.push(newMetaData);
                }

            });
            console.timeEnd(`SAVING REPORTING METADATA`);    

            return Maybe.fromNull(f);

        };

        // Monad input value
        const form = {  };

        // MAINLINE: this drives composition of the form state form listService into a form object.
        // Command pattern implemented via a monad.
        // Its result value is either form JSON or nothing.
        // Maintaintenance folks: okay to rearrange some chained items as needed.
        const formObj =  Maybe.fromNull( form )
            .chain( formRoot )
            .chain( formGuid )
            .chain( formDocVersion )
            .chain( formVersion )
            .chain( formProperties )
            .chain( states )
            .chain( objects )
            .chain( pages )
            .chain( startPages )
            .chain( theme )
            .chain( workflow )
            .chain( assets )
            .chain( multilanguage )
            .chain( advancedCSS )
            .chain( datasources )
            .chain( dependencies )
            .chain( parameters )
            .chain( formEvents )
            .chain( reportingMetaData )
            .toEither( )
            .cata( ( ) => Nothing, f  =>  f );

        return formObj;

    }

    marshalMetadata(form){

        return formMetadataService.renderMetadata(form);
    }

    //==============================================================================================
    // Saves the current form held by the listService arg. Accepts an optional saveFn "operator"
    // function that specializes the kind of save. Defaults to saveFormOperator.
    // Note: to save a new version, override default option, isNewVerion to true.
    //
    // Returns an ES6 promise of a success message if successful, or an error message if failure.
    //==============================================================================================
    saveCurrentForm( options = {}) {
        //make sure we are in Desktop Mode while Saving
        const currentDevice = formModel.get('currentDevice');
        formModel.set({
            currentDevice: 'deviceDesktop'
        });

        const saveLayoutOrMobile = function ( ) {
            return ((!listService.isMobileOpen || listService.isRecoverySave) && listService.mainKind !== 'M' || listService.isDirty || options.isNewVersion || options.currentFormAsNew);
        }


        const defaultOpts = {
            saveFn: saveLayoutOrMobile()  ? self.operatorSaveCurrentForm : self.operatorSaveCurrentMobileForm,
            isNewVersion: false,
            groupId: constants.DEFAULT_GROUP_ID,
            isReport: false
        };
        const opts = Object.assign({}, defaultOpts, options);
        const root = self.docRoot;
        self.isMobileEnabled = userService.getUserFeatures().MobileViews;

        console.log('opts', opts);

        // Define "Maybe-monad" worker functions below. Appearing in order of invocation for sane top-to-bottom reading

        const isFormGUID = function( d )  {
            d.formGUID = listService.formBase.guid;
            if ( !d.formGUID ) {
                const msg = `This document was never added to in the PerfectApps service.`;
                console.log( msg );
                d.emsg.push( msg );
                notificationService.error( msg );
                // Short-circuit succeeding chained callbacks because we're wounded
                return Nothing;
            }
            return Maybe.fromNull( d );
        };

        const createFormObj = function( d )  {
            d.work = self.marshalForm( d.ls );
            return Maybe.fromNull( d );
        };

        const createFormXml = function( d )  {

            d.formXML = xjService.xmlToString( formulaService.postProcess( d.work ) );
            // clean any possible emoji
            d.formXML = d.formXML.replace(/([\uE000-\uF8FF]|\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDDFF])/g, '');
            
            //PF-5550 replace one of the tags so the xml does not break, this is not done before because the xmlToStringFunction 
            //changes the & to &amp; and it was causing the connect & search message to not be displayed correctly on player
            d.formXML = d.formXML.replace(/<br>/ig, '&lt;br>'); 
            
            console.log( d.formXML );
            return Maybe.fromNull( d );
        };

        const compressFormXML = function( d )  {

            d.formXML = zLib.compressForm(d.formXML);
            return Maybe.fromNull( d );
        };

        const createMetaDataXML = function (d) {
            const id = d.ls.currentForm.model.get('id');
            const version = d.ls.currentForm.model.get('version');
            const docVersion = d.ls.currentForm.model.get('docVersion');
            if (self.docRoot.toLowerCase() === 'form') {
                console.log('on createMetaDataXML: ', d.work);

                const metaData = {};
                metaData.ResponseMetaData = {};
                metaData.ResponseMetaData = self.marshalMetadata(d.work);
                metaData.ResponseMetaData._id = id;
                metaData.ResponseMetaData._version = version;
                metaData.ResponseMetaData._docVersion = docVersion;
                console.log('metadata: ', metaData);
                d.work = metaData;
                let metaDataXML = xjService.xmlToString(xjService.jsToXml(d.work));
                console.log('metadataXML: ' + metaDataXML);
                d.work = metaDataXML;//`<ResponseMetaData id="${id}" version="${version}" docVersion="${docVersion}"><Fields/></ResponseMetaData>`;
            } else {
                d.work = `<ResponseMetaData id="${id}" version="${version}" docVersion="${docVersion}"><Fields/></ResponseMetaData>`;
            }
            
            return Maybe.fromNull(d);
        };

        const compressMetaDataXML = function( d ) {
            d.metaDataXML = zLib.compressForm(d.work);
            return Maybe.fromNull( d );
        };

        const createMobileSettings = function( d ) {  
            if (self.isMobileEnabled && listService.isMobileDirty || listService.timesWizardCompleted || listService.mobileObjects.size) {
                const mobileSettings = mobileSaveService.createMobileSettings(d.ls);
                const xmlMobileSettings = xjService.toXML(mobileSettings);
                d.mobileSettings = zLib.compressForm(xmlMobileSettings);
            }
            return Maybe.fromNull( d );
        };

        const createMobileViewObj = function( d ) {  
            if (self.isMobileEnabled && listService.isMobileDirty || listService.timesWizardCompleted || listService.mobileObjects.size) {
                d.mobileForm = mobileSaveService.marshalMobileViews(d.ls);
            }
            return Maybe.fromNull( d );
        };

        const createViewXml = function( d )  {
            if (self.isMobileEnabled  && listService.isMobileDirty || listService.timesWizardCompleted || listService.mobileObjects.size) {
                const xmlMobile = xjService.toXML(d.mobileForm);
                d.mobileView = xmlMobile;
            }

            return Maybe.fromNull( d );
        };

        const compressViewXml = function( d )  {

            if(self.isMobileEnabled && listService.mobileSectionsExist() && (listService.isMobileDirty || listService.mobileGetWizardCompleted)){                
                d.mobileXML = zLib.compressForm(d.mobileView);
                d.mobileForm = undefined;
                console.log(`SAVING Mobile Form`, d.mobileView);                
                
            }else if(!listService.mobileSectionsExist()){
                d.mobileXML = 'REMOVE';
                listService.resetCurrentMobileForm = false;
            }
            return Maybe.fromNull( d );
        };

        const createMobileColumns = function( d )  {
            if (self.isMobileEnabled && (listService.isMobileDirty || listService.isRecoverySave) && listService.existSecondColumnConfiguration()) {
                const twoColumns = mobileSaveService.createMobileTwoColumn(d.ls);
                const xmlTwoColumns = xjService.toXML(twoColumns);
                console.log(`SAVING Mobile Form two Columns `, xmlTwoColumns);
                d.mobileColumns = zLib.compressForm(xmlTwoColumns);
            } else {
                if(!listService.existSecondColumnConfiguration()){
                    d.mobileColumns = 'REMOVE';
                }
            }
            return Maybe.fromNull( d );
        };

        // This is for REST forms API parameters.
        const optionsFn = function ( d ) {
            d.groupId = opts.groupId;
            d.isNewVersion = opts.isNewVersion;
            d.work = undefined;
            return Maybe.fromNull( d );
        };



        // Holds compressed formXML and metaDataXML ...
        // Creates the working object for a Maybe-monad.
        // It will be handed from helper-to-helper by the monad.
        const data = {};
        data.promise    = undefined;
        data.emsg       = [];

        data.formXML        = undefined;
        data.metaDataXML    = undefined;
        data.formGUID       = undefined;
        data.width          = undefined;
        data.height         = undefined;
        data.isNewVersion   = undefined;
        data.groupId        = undefined;
        data.work           = undefined;
        data.ls             = listService;
        data.isPreview = options.isPreview;
        data.mobileForm = undefined;
        data.mobileColumns = undefined;  
        data.mobileSettings = undefined;

        // Populate data's formXml and metaDataXML
        let r;
        console.time('Overall process of SAVE');
        if (saveLayoutOrMobile()) {
            console.log("save form");
            r = Maybe.fromNull( data )
                .chain( isFormGUID )
                .chain( createFormObj )
                .chain( createFormXml )
                .chain( compressFormXML )
                .chain( createMetaDataXML )
                .chain( compressMetaDataXML )
                .chain( createMobileSettings )
                .chain( createMobileViewObj )
                .chain( createMobileColumns )
                .chain( createViewXml )
                .chain( compressViewXml )
                .chain( optionsFn )
                .chain( opts.saveFn );  // Sets data.promise
        } else if (listService.isMobileOpen || listService.mainKind === 'M') {

            console.log("save wizard");
            r = Maybe.fromNull( data )
                .chain( createMobileSettings )
                .chain( createMobileViewObj )
                .chain( createMobileColumns )
                .chain( createViewXml )
                .chain( compressViewXml )
                .chain( optionsFn )
                .chain( opts.saveFn );  // Sets data.promise
        }
        console.timeEnd('Overall process of SAVE');
        listService.isPartialSave = true; //return this value to its default state

        if ( r.isValue ) {
            // Return only promise and messages
            delete r.val.formXML;
            delete r.val.metaDataXML;
            delete r.val.formGUID;
            delete r.val.reportGUID;
            delete r.val.width;
            delete r.val.height;
            delete r.val.isNewVersion;
            delete r.val.groupId;
            delete r.val.work;
            delete r.val.ls;
            delete r.val.mobileForm;
            delete r.val.mobileColumns;
            delete r.val.mobileSettings;
        }

        // Switch back to the device layout we started with.
        formModel.set({
            currentDevice: currentDevice
        });

        // NOTE: conditioned by  r.isValue,
        return r.val.promise;
    }



    //==============================================================================================
    // Passes a custom save operator function to saveForm. The custom function requests a save as
    // new form from the REST service.
    //
    // Result is a promise.
    //==============================================================================================
    saveCurrentFormAsNew( ) {
        // Override save operator passed to saveForm:
        return this.saveCurrentForm( { saveFn : this.operatorSaveCurrentFormAsNew, currentFormAsNew: true } );
    }


    //==============================================================================================
    // Passes a custom save operator function to saveCurrentForm. The custom function implements
    // saving the current report from the REST service.
    // Note: to save a new version, override default option, isNewVersion to true.
    //
    // Result is a promise.
    //==============================================================================================
    saveCurrentReport( options ) {
        const defaultOpts = {
            saveFn: self.operatorSaveCurrentReport,
            isNewVersion: false,
            groupId: constants.DEFAULT_GROUP_ID,
            isReport: true
        };
        const opts = Object.assign({}, defaultOpts, options);

        (function logme() {
            console.log('opts', opts);
            console.log('formBase', listService.formBase);
            console.log(`doc`, listService.objectList.toJS());
        })();

        // Override save operator passed to saveForm:
        return self.saveCurrentForm( opts );
    }

    //==============================================================================================
    // Passes a custom save operator function to saveForm. The custom function implements saving
    // the current report as a new version, from the REST service.
    //
    // Result is a promise.
    //==============================================================================================
    saveCurrentReportAsNew( ) {
        // Override save operator passed to saveForm:
        return this.saveCurrentForm( { saveFn : this.operatorSaveCurrentReportAsNew, isReport: true } );
    }


    // Pass this property as an options.saveFn arg to base save function: FormSaveService.saveCurrentForm.
    // Value is a promise.
    get operatorSaveCurrentFormAsNew() {
        return function( d ) {

            d.promise = new Promise((resolve, reject) => {

                const params = {};
                params.formId       = d.ls.formBase.id;
                params.formGUID     = undefined; // REST service sets this
                params.formXML      = d.formXML;
                params.metaDataXML  = d.metaDataXML;
                params.name         = d.ls.currentForm.model.get('name');
                params.description  = d.ls.currentForm.model.get('name');
                params.width        = Number(d.ls.currentForm.model.get('formWidth'));
                params.height       = Number(d.ls.currentForm.model.get('formHeight'));
                params.timeOut      = constants.TIMEOUT_SECONDS;
                params.dependenciesXML = zLib.compressForm(self.getDependenciesXML());
                 
                if (util.isTrue(listService.currentForm.model.attributes.frame)) {
                    params.width = params.width + (Number(listService.currentForm.model.attributes.frameSize)*2);  
                    params.height = params.height + (Number(listService.currentForm.model.attributes.frameSize)*2);
                }

                if (util.isTrue(listService.currentForm.model.attributes.shadow)) {
                    params.width = params.width + 10;  
                    params.height = params.height + 10;
                }
                   
                params.width = params.width.toString();
                params.height = params.height.toString();

                // Mobile View Params            
                params.mobileEnabled = userService.getUserFeatures().MobileViews.toString();
                params.wizardCompleted = listService.mobileGetWizardCompleted;
                params.formXMLLayout1Col = d.mobileXML;
                params.formXMLLayout2Col = d.mobileColumns;
                params.mobileSettings = d.mobileSettings;


                // Request HTTP POST.
                restService.saveAsNewForm(params, d.groupId).then(data => {
                    listService.currentForm.model.set('id', data.formId, {silent:true});
                    listService.currentForm.model.set('name', data.name, {silent:true});
                    //listService.currentForm.model.set('title', data.name, {silent:true});
                    listService.currentForm.model.set('guid', data.formGUID, {silent:true});
                    listService.formBase.id = data.formId;
                    listService.formBase.name = data.name;
                    listService.formBase.guid = data.formGUID;

                    const goodMsg = `App version ${data.majorVersion}.${data.minorVersion} saved.`;
                    util.disableSaveOption();
                    resolve({name: data.name, msg: goodMsg});

                }, err => {
                    console.log(err);
                    if (err && typeof err === 'string') {
                        reject(err);
                    } else if (err && typeof err === 'object' && err.responseText) {
                        const msg = JSON.parse(err.responseText);
                        reject(`${msg.Message} ${msg.ExceptionMessage}`);
                    } else {
                        reject(err);
                    }
                });

            });
            return Maybe.fromNull( d );
        };
    }


    // Pass this property as an options.saveFn arg to base save function: FormSaveService.saveCurrentForm.
    // Value is a promise.
    get operatorSaveCurrentForm() {
        return function( d ) {
            d.promise = new Promise((resolve, reject) => {
                
                // PARAMS: SAVE FORM
                //---------------------------------------------------------------------
                // token	text	the authentication token	8640697470460952576
                // *formGUID	text	the form GUID	1FongwAAH0sAAJhGAADDS+PIWdZ
                // *formId	text	the form Id	16233
                // *formXML	text	the form XML	(encoded XML)
                // width	text	width of the form	512
                // height	text	height of the form	412
                // close	text	close the form after save	false
                // isNewVersion	text	save as new version if it is true	false
                // metaDataXML	text	the meta data XML	H4sIAAAAAAAA/3jaswlKLS7IzytO9U0tSXR ....
                // settingsXML	text	the user's settings	-9223372036854775808
                // timeOut	text	the time out for locking an instance	30
                // logOut	text	if the user should be logged out	false
                // dependenciesXML	text

                const params = {};
                params.formId       = d.ls.formBase.id;
                params.formGUID     = d.ls.formBase.guid;
                params.formXML      = d.formXML;
                params.width        = Number(d.ls.currentForm.model.get('formWidth'));
                params.height       = Number(d.ls.currentForm.model.get('formHeight'));
                params.metaDataXML  = d.metaDataXML;
                params.isNewVersion = d.isNewVersion;  // PF-617
                params.timeOut      = constants.TIMEOUT_SECONDS;
                params.close        = 'true';    // TODO parameterize close? Also, why doesn't this close the form?
                params.settingsXML  = zLib.compressForm(userService.populateUserSettingsFromApp());
                params.logOut       = 'false';
                params.containsMobileErrors = d.ls.currentForm.model.get('containsMobileErrors');
                params.dependenciesXML = zLib.compressForm(self.getDependenciesXML());
                 
                if (util.isTrue(listService.currentForm.model.attributes.frame)) {
                    params.width = params.width + (Number(listService.currentForm.model.attributes.frameSize)*2);  
                    params.height = params.height + (Number(listService.currentForm.model.attributes.frameSize)*2);
                }

                if (util.isTrue(listService.currentForm.model.attributes.shadow)) {
                    params.width = params.width + 10;  
                    params.height = params.height + 10;
                }
                   
                params.width = params.width.toString();
                params.height = params.height.toString();
                
                // Mobile View Params            
                params.mobileEnabled = userService.getUserFeatures().MobileViews.toString();
                params.wizardCompleted = listService.mobileGetWizardCompleted;
                params.formXMLLayout1Col = d.mobileXML;
                params.formXMLLayout2Col = d.mobileColumns;
                params.mobileSettings = d.mobileSettings;

                //PF-10864 make sure reflow value exists, this caused issues with reports
                if(typeof listService.currentForm.model.attributes.reflowEnabled !== 'undefined'){
                    params.reflowEnabled = listService.currentForm.model.attributes.reflowEnabled.toString();
                }

                //send majorVersion and minorVersion to copy on DB the mobile form when is not sent
                if(d.isNewVersion && userService.getUserFeatures().MobileViews){
                    params.oldMajorVersion = listService.formBase.majorVersion;
                    params.oldMinorVersion = listService.formBase.minorVersion;
                }

                const isRecoverySave = listService.isRecoverySave;

                // Request HTTP POST.
                restService.saveForm(params, d.groupId).then(data => {
                    const goodMsg = `App version ${data.majorVersion}.${data.minorVersion} saved.`;
                    
                    listService.isDirty = false;
                    if (!isRecoverySave){
                        util.disableSaveOption();
                    }
                    resolve(goodMsg);

                }, err => {
                    console.log(err);
                    if (err && typeof err === 'string') {
                        reject(err);
                    } else if (err && typeof err === 'object' && err.responseText) {
                        const msg = JSON.parse(err.responseText);
                        reject(`${msg.Message} ${msg.ExceptionMessage}`);
                    } else {
                        reject(err);
                    }
                });

            });

            //Reset flag for mobile
            listService.isMobileDirty = false;

            return Maybe.fromNull(d);
        };
    }


    get operatorSaveCurrentMobileForm() {
        return function( d ) {
            d.promise = new Promise((resolve, reject) => {

                // PARAMS: SAVE FORM
                //---------------------------------------------------------------------
                // token	text	the authentication token	8640697470460952576
                // *formId	text	the form Id	16233
                // isNewVersion	text	save as new version if it is true	false
                // metaDataXML	text	the meta data XML	H4sIAAAAAAAA/3jaswlKLS7IzytO9U0tSXR ....

                const params = {};
                params.formId       = d.ls.formBase.id;

                params.containsMobileErrors = d.ls.currentForm.model.get('containsMobileErrors');

                // Mobile View Params
                params.mobileEnabled = userService.getUserFeatures().MobileViews.toString();
                params.wizardCompleted = listService.mobileGetWizardCompleted;
                params.formXMLLayout1Col = d.mobileXML;
                params.formXMLLayout2Col = d.mobileColumns;
                params.mobileSettings = d.mobileSettings;
                params.isNewVersion = d.isNewVersion;  // PF-617

                //send majorVersion and minorVersion to copy on DB the mobile form when is not sent
                if(d.isNewVersion && userService.getUserFeatures().MobileViews){
                    params.oldMajorVersion = listService.formBase.majorVersion;
                    params.oldMinorVersion = listService.formBase.minorVersion;
                }

                // Request HTTP POST.
                restService.saveMobileForm(params, d.groupId).then(data => {
                    const goodMsg = `App version ${data.majorVersion}.${data.minorVersion} saved.`;
                    util.disableSaveOption();
                    resolve(goodMsg);

                }, err => {
                    console.log(err);
                    if (err && typeof err === 'string') {
                        reject(err);
                    } else if (err && typeof err === 'object' && err.responseText) {
                        const msg = JSON.parse(err.responseText);
                        reject(`${msg.Message} ${msg.ExceptionMessage}`);
                    } else {
                        reject(err);
                    }
                });

            });

            //Reset flag for mobile
            listService.isMobileDirty = false;
            
            return Maybe.fromNull(d);
        };
    }

    //PF-2731
    getDependenciesXML() {
        const _dependencies = listService.getDependencies();
        let _Dependencies = { Dependencies: {} };
        _Dependencies.Dependencies.Dependency = [];
        let Dependency;

        if (_dependencies && _dependencies.length > 0) {
            $.each(_dependencies, function(idx, _dependency){
                Dependency = {};
                Dependency._id = _dependency.id;
                Dependency._type = _dependency.type;
                Dependency._caption = _dependency.caption;
                _Dependencies.Dependencies.Dependency.push(Dependency);
            });
        }

        let dependenciesXML = xjService.xmlToString(xjService.jsToXml(_Dependencies));
        
        return dependenciesXML;
    }


    // Pass this property as an options.saveFn arg to base save function: FormSaveService.saveCurrentForm.
    // Value is a promise.
    get operatorSaveCurrentReport() {
        return function( d ) {
            console.log(`operatorSaveCurrentReport entered.`);
            d.promise = new Promise((resolve, reject) => {

                // PARAMS: SAVE REPORT
                //---------------------------------------------------------------------
                // token	    text	The authentication token	1795268767168528384
                // *reportId	int	    Report id	16283
                // *reportGUID	text	The report GUID	NPR0cgAAw9YABDNRAABL5Jj/sXM=
                // *reportXML	text	The report XML.	ENCRYPTED REPORT XML
                // isNewVersion	bool	Saves as a new version if it's true.	true
                // width	    Int16	The width of the form	512
                // height	    Int16	The height of the form	412
                // close	    bool	Close report after save	false
                // settingsXML	text	The user's settings	-9223372036854775808
                // logOut	    bool	If the user should be logged out	false
                // dependenciesXML	text	The report's dependencies	H4sIAAAAAAAA/3jas3FJLUjNS0nNS85MLda3AwAr5QVxwUDSDg8AAAA=
                const params = {};
                params.reportId     = d.ls.formBase.id;
                params.reportGUID   = d.ls.formBase.guid;
                params.reportXML    = d.formXML;
                params.isNewVersion = d.isNewVersion;
                params.width        = d.ls.currentForm.model.get('formWidth');
                params.height       = d.ls.currentForm.model.get('formHeight');
                params.close        = true;    // TODO parameterize close? Also, why doesn't this close the form?
                params.settingsXML  = zLib.compressForm(userService.populateUserSettingsFromApp());
                params.logOut       = false;
                params.containsMobileErrors = d.ls.currentForm.model.get('containsMobileErrors');
                params.dependenciesXML = zLib.compressForm(self.getDependenciesXML());
                params.isPreview = d.isPreview;

                // Mobile View Params           
                params.mobileEnabled = userService.getUserFeatures().MobileViews.toString();
                params.reportXMLLayout = undefined;

                const isRecoverySave = listService.isRecoverySave;

                // Request HTTP POST.
                restService.saveReport(params, d.groupId).then(data => {

                    const goodMsg = `Report version ${data.majorVersion}.${data.minorVersion} saved.`;
                    listService.isDirty = false;
                    
                    if (!isRecoverySave) {
                        util.disableSaveOption();
                    }
                    resolve(goodMsg);

                }, err => {
                    console.log(err);
                    if (err && typeof err === 'string') {
                        reject(err);
                    } else if (err && typeof err === 'object' && err.responseText) {
                        const msg = JSON.parse(err.responseText);
                        reject(`${msg.Message} ${msg.ExceptionMessage}`);
                    } else {
                        reject(err);
                    }
                });

            });

            //Reset flag for mobile
            listService.isMobileDirty = false;

            return Maybe.fromNull(d);
        };
    }


    // Pass this property as an options.saveFn arg to base save function: FormSaveService.saveCurrentForm.
    // Value is a promise.
    get operatorSaveCurrentReportAsNew() {
        return function( d ) {
            console.log(`operatorSaveCurrentReportAsNew entered.`);
            d.promise = new Promise((resolve, reject) => {

                const hasResponsive = AccountService.verifyResponsive();

                // PARAMS: SAVE REPORT VERSION
                //---------------------------------------------------------------------
                // reportId	        int	Report id	16283
                // reportGUID	    text	The report GUID	gzqdVAAAbGYAAA7eAACNI8AXHQ4=
                // reportXML	    text	The report XML.	ENCRYPTED REPORT XML
                // name	            text	The name of the report	My New Report
                // description	    text	The Report's description
                // width	        Int16	The width of the form	512
                // height	        Int16	The height of the form	412
                // dependenciesXML	text	The report's dependencies	H4sIAAAAAAAA/3jas3FJLUjNS0nNS85MLda3AwAr5QVxwUDSDg8AAAA=

                const guid = d.ls.formBase.guid = guidUtil.encodedGuid;

                const params = {};
                params.reportId = d.ls.formBase.id;
                params.reportGUID = d.ls.formBase.guid;
                params.reportXML = d.formXML;
                params.metaDataXML = d.metaDataXML;
                params.name = d.ls.currentForm.model.get('name');
                params.description = d.ls.currentForm.model.get('name');
                params.width = d.ls.currentForm.model.get('formWidth');
                params.height = d.ls.currentForm.model.get('formHeight');
                params.dependenciesXML = zLib.compressForm(self.getDependenciesXML())

                if (hasResponsive) {
                    // Mobile View Params
                    const mobileParams = {};
                    mobileParams.reportId = d.ls.formBase.id;
                    mobileParams.viewTabletXML = d.mobileTabletView;
                    mobileParams.viewPhoneXML = d.mobilePhoneView;
                }

                // Request HTTP POST.
                restService.reportSaveNew(params, d.groupId).then(data => {

                    if (hasResponsive) {
                        // TODO Call Endpoint to upload Mobile Views
                    }
                    util.disableSaveOption();

                    //Update currentForm with the new formId 
                    listService.currentForm.model.set('id', data.reportId, {silent:true});
                    listService.currentForm.model.set('name', data.name, {silent:true});
                    //listService.currentForm.model.set('title', data.name, {silent:true});
                    listService.formBase.id = data.reportId;
                    listService.formBase.name = data.name;
                    const goodMsg = `Report version ${data.majorVersion}.${data.minorVersion} saved.`;

                    resolve({name: data.name, msg: goodMsg});

                }, err => {
                    console.log(err);
                    if (err && typeof err === 'string') {
                        reject(err);
                    } else if (err && typeof err === 'object' && err.responseText) {
                        const msg = JSON.parse(err.responseText);
                        reject(`${msg.Message} ${msg.ExceptionMessage}`);
                    } else {
                        reject(err);
                    }
                });

            });

            return Maybe.fromNull(d);
        };
    } 


    /**
     * ================================================================================
     * PF-11366 Refactored Save Service
     * ================================================================================
     * This refactored code centralizes the save logic to eliminate redundancy and
     * improve maintainability. It introduces a single entry point `handleSave` that manages all save scenarios.
     */

    async handleSave(options, context) {
        const { saveType, preview = false, debug = false, publish = false, onClose } = options;

        const currentForm = listService.currentForm;
        const isRecoverySave = listService.isRecoverySave;
        const spinner = !isRecoverySave ? new Spinner().spin($(constants.CONTENT_ID).get(0)) : undefined;

        const updateAppName = () => {
            return new Promise((resolve, reject) => {
                const currentForm = listService.currentForm;
                const formId = currentForm.model.get("id");
                const appName = currentForm.model.get("formNameFromResponse");
                const newAppName = listService.formBase.name;

                if (newAppName && newAppName !== "null" && newAppName !== appName) {
                    //Update form name
                    restService.updateFormName(formId, newAppName).then(data => {
                        const validatedName = data.RESULT.RESPONSE.Name;
                        currentForm.model.set("formNameFromResponse", validatedName);
                        resolve("ok");
                    }, err => {
                        console.log(data.msg, 'error');
                        notificationService.error("Error saving app. The app name should not contain invalid characters.");
                        resolve("error changing name");
                    });
                }
                else {
                    resolve("ok");
                }
            });
        }

        const prepareForSave = async () => {
            if (currentForm.model.get('title') === currentForm.model.get('name')) {
                currentForm.model.set('title', null, { silent: true });
            }
            currentForm.model.set('isYodaFile', true, { silent: true });
            userService.stopCheckingUserSession();
            await updateAppName();
        };

        const executeCoreSave = (executeOptions) => {
            return new Promise((resolve, reject) => {
                const savePromise = executeOptions.isNewApp
                    ? this.saveCurrentFormAsNew()
                    : this.saveCurrentForm(executeOptions);

                savePromise.then(result => {
                    const msg = executeOptions.isNewApp ? result.msg : result;

                    if (executeOptions.isNewApp) {
                        util.setAppNewName(result.name);
                        listService.currentForm.model.set("name", result.name);
                    }

                    if (executeOptions.isNewVersion) {
                        const version = msg.split(" ")[2];
                        const isPublished = util.isTrue(listService.dashboardLayout.mainPanelView.model.get("IsDeployed"));
                        util.updateAppReportVersion(version, isPublished, false);
                    }
                    resolve(msg);

                }).catch(errInfo => {
                    if (parseInt(errInfo[1]) === 16) { // Check for "already published" error
                        const confirmText = 'You cannot save over a published version. <br /><br />Would you like to save your changes as a new version?';
                        const confirmView = new ConfirmDialogView({
                            model: new Model({
                                text: confirmText,
                                height: 170,
                                title: 'App Already Published',
                                success: () => {
                                    executeCoreSave({ ...executeOptions, isNewVersion: true })
                                        .then(resolve)
                                        .catch(reject);
                                },
                                cancel: () => reject(0)
                            })
                        });
                        confirmView.render();
                    } else {
                        reject(errInfo[0] || errInfo);
                    }
                });
            });
        };

        const finalizeSave = async () => {
            if (!isRecoverySave) {
                undoService.clearChangeTracker();
                if (publish) {
                    if (context)
                        context.publishForm();
                } else {
                    if (context)
                        context.showDebugOrPreview(preview, debug);
                }
            } else {
                listService.setDirtyRecovery(false);
            }
        };

        try {
            await prepareForSave();

            const saveOptions = {
                isNewVersion: saveType === 'newVersion',
                isNewApp: saveType === 'newApp',
                isMobileDirty: listService.isMobileDirty,
            };

            const successMsg = await executeCoreSave(saveOptions);

            if (!isRecoverySave) {
                notificationService.success(successMsg);
            }

            // Await the finalization step to ensure publish messages are shown.
            await finalizeSave();

            // Now, execute the close action after everything else is done.
            if (onClose && typeof onClose === 'function') {
                onClose();
            }
        } catch (error) {
            if (!isRecoverySave && error !== 0) {
                notificationService.error(error);
            }
            throw error;
        } finally {
            userService.checkUserSession();
            spinner && spinner.stop();
        }
    }
    
}

const formSaveService = new FormSaveService();
export default formSaveService;
